/**
 * ParticleSystem — one long-lived instanced quad, a ring buffer of particles
 * with real physics, simulated on the CPU and uploaded as one range.
 *
 * ── Why a ring buffer and not a burst pool ─────────────────────────────────
 *
 * The burst pool it replaces (`ScreenEffects.spawnParticles`) made every burst
 * its own mesh and draw call, capped at 18–64 concurrent BURSTS per tier, and
 * evicted whole effects when full — a busy fight deleted the ultimate you just
 * fired because someone landed a basic. Here a system is ONE mesh for its whole
 * life; a fight is one draw per system however many effects are in flight, and
 * being full costs the OLDEST PARTICLE, never a whole effect.
 *
 * ── Physics ────────────────────────────────────────────────────────────────
 *
 * Every particle carries velocity, exponential drag (`exp(-drag·dt)`), gravity,
 * a ground plane it bounces off (restitution −0.35, so debris skitters instead
 * of sinking through the deck), and a size that lerps `size0 → size1`. All of
 * it is driven by the `dt` handed to `update()`: 0 during hitstop, and the
 * particles hang in the air.
 *
 * ── Four shapes, three ways to die ─────────────────────────────────────────
 *
 *   GLOW   additive soft-cored disc — the only additive thing in the kit, kept
 *          for tiny hot sparks and cores where a glow is honest.
 *   SMOKE  the old soft alpha puff (kept for callers that want a haze).
 *   PUFF   the cartoon cloud: a blobby, hard-edged, cel-shaded ball with an ink
 *          rim and a highlight, dying by EROSION (a noise field eats it with a
 *          bright front) instead of fading.
 *   SHARD  an angular chunk with a white core, saturated body and ink rim,
 *          heading along its velocity when fast, tumbling when slow.
 *
 *   fade: 'alpha'  alpha follows 1 − t² (sparks: full until late, then gone)
 *         'erode'  the w channel carries PROGRESS and the shader erodes on it
 *         'cut'    full alpha to the end; the shape dies by its size lerp
 *
 * ── Colour ─────────────────────────────────────────────────────────────────
 *
 * The colour attribute is linear and unclamped. GLOW recipes push it above the
 * bloom threshold; PUFF and SHARD take a plain LDR identity colour and build
 * their core/body/ink ramp from it in the shader.
 *
 * Allocation-free after construction. Node materials only (WebGPU).
 */

import * as THREE from 'three/webgpu';
import {
  uv,
  float,
  vec2,
  vec4,
  smoothstep,
  select,
  atan,
  mix,
  sin,
  hash,
  time,
  instanceIndex,
  modelViewMatrix,
  instancedDynamicBufferAttribute,
} from 'three/tsl';
import { cloud, erode, palette } from '../CelLook.js';

/** Floats per particle in the uploaded buffer. */
export const STRIDE = 12;
const POS = 0; // vec3
const SIZE = 3; // float, world units (diameter)
const RGBA = 4; // vec4, rgb + alpha (or progress, for erode systems)
const VEL = 8; // vec3, units/s (read only by the stretch path)
const STRETCH = 11; // float, 0 = round sprite

/** CPU-only state per particle, never uploaded. */
const SIM = 9;
const LIFE = 0; // seconds remaining
const MAX_LIFE = 1;
const SIZE0 = 2;
const SIZE1 = 3;
const ALPHA0 = 4;
const DRAG = 5;
const GRAV = 6;
const FLOOR = 7; // ground y this particle bounces off
const BOUNCE = 8; // restitution (0 = passes through)

/** How far above the floor a bounced particle settles. */
const FLOOR_SKIN = 0.03;

export const SHAPES = Object.freeze({ GLOW: 'glow', SMOKE: 'smoke', PUFF: 'puff', SHARD: 'shard' });
export const FADES = Object.freeze({ ALPHA: 'alpha', ERODE: 'erode', CUT: 'cut' });

function buildMaterial({ additive, shape, fade, buffer }) {
  const pos = instancedDynamicBufferAttribute(buffer, 'vec3', STRIDE, POS);
  const size = instancedDynamicBufferAttribute(buffer, 'float', STRIDE, SIZE);
  const rgba = instancedDynamicBufferAttribute(buffer, 'vec4', STRIDE, RGBA);
  const vel = instancedDynamicBufferAttribute(buffer, 'vec3', STRIDE, VEL);
  const stretch = instancedDynamicBufferAttribute(buffer, 'float', STRIDE, STRETCH);

  const material = new THREE.SpriteNodeMaterial();
  material.positionNode = pos;

  // Per-instance seed: the slot index hashed. Stable for a particle's life.
  const seed = hash(instanceIndex);

  // View-space velocity, w = 0 so the camera's translation stays out of a
  // direction. The mesh never moves, so modelView is the view matrix.
  const v = modelViewMatrix.mul(vec4(vel, 0.0)).xy;
  const speed = v.length();
  const stretched = stretch.greaterThan(0.0).and(speed.greaterThan(1e-4));
  // The quad's +y is the long axis; `rotate` maps +y to (-sin, cos), hence
  // atan2(-x, y). A particle heading straight at the camera has no screen
  // heading and stays upright rather than putting a NaN through the vertex.
  const heading = atan(v.x.negate(), v.y);
  if (shape === SHAPES.SHARD) {
    // A slow shard tumbles; a fast one flies point-first.
    const spin = seed.mul(6.2832).add(time.mul(seed.sub(0.5).mul(14)));
    material.rotationNode = select(stretched, heading, spin);
  } else if (shape === SHAPES.PUFF) {
    material.rotationNode = seed.mul(6.2832);
  } else {
    material.rotationNode = select(stretched, heading, float(0));
  }
  const along = float(1).add(stretch.mul(speed).clamp(0, 4));
  material.scaleNode = vec2(size, size.mul(select(stretched, along, float(1))));

  const local = uv().sub(0.5).mul(2);
  const d = local.length();
  const w = rgba.w;
  let alpha;
  let color = rgba.xyz;
  if (shape === SHAPES.PUFF) {
    const c = palette(rgba.xyz);
    const ang = atan(local.x, local.y);
    // A blobby outline: three and five lobes, phased per instance.
    const R = float(0.84).add(sin(ang.mul(3).add(seed.mul(9))).mul(0.09)).add(sin(ang.mul(5).sub(seed.mul(5))).mul(0.05));
    const q = d.div(R);
    const mask = smoothstep(float(1.0), float(0.95), q);
    const ink = smoothstep(float(0.74), float(0.82), q);
    // Lit from above-left: a darker underside and a highlight blob.
    const shade = smoothstep(float(0.3), float(-0.5), local.y);
    let col = mix(c.body, c.deep, shade);
    const hi = smoothstep(float(0.55), float(0.2), local.sub(vec2(-0.22, 0.32)).length());
    col = mix(col, c.hot, hi.mul(0.85));
    col = mix(col, c.ink, ink);
    const n = cloud(local.mul(1.6), seed.mul(20));
    const e = erode(n, w, { edge: 0.05, front: 0.14 });
    color = mix(col, c.core, e.front.mul(0.9));
    alpha = mask.mul(e.alive);
  } else if (shape === SHAPES.SHARD) {
    const c = palette(rgba.xyz);
    // A kite, pointier along +y so it has a nose when it flies.
    const ky = local.y.abs().mul(select(local.y.greaterThan(0), float(0.72), float(1.05)));
    const kd = local.x.abs().mul(1.45).add(ky);
    const mask = smoothstep(float(1.0), float(0.94), kd);
    const ink = smoothstep(float(0.74), float(0.82), kd);
    let col = mix(c.body, c.hot, smoothstep(float(0.5), float(0.3), kd));
    col = mix(col, c.core, smoothstep(float(0.22), float(0.12), kd));
    // A lit facet on one side.
    col = mix(col, c.deep, smoothstep(float(0.1), float(0.5), local.x).mul(0.5).mul(float(1).sub(ink)));
    col = mix(col, c.ink, ink);
    color = col;
    if (fade === FADES.ERODE) alpha = mask.mul(erode(cloud(local.mul(2.2), seed.mul(20)), w).alive);
    else if (fade === FADES.CUT) alpha = mask;
    else alpha = mask.mul(w);
  } else if (shape === SHAPES.SMOKE) {
    // A cartoon puff: solid to ~60% of the radius, a short skirt, a slightly
    // lifted heart so a pile of puffs has depth instead of being one blot.
    alpha = smoothstep(float(1.0), float(0.6), d).mul(w);
    color = mix(color, color.mul(1.25), smoothstep(float(0.75), float(0.0), d));
  } else {
    // A glow disc with a FAT core: solid to a third of the radius, soft to the
    // edge. At 8–12 px on screen a gaussian is a smudge; this reads as a hot dot
    // and lets the bloom paint the halo.
    alpha = smoothstep(float(1.0), float(0.32), d).mul(w);
  }
  material.colorNode = color;
  material.opacityNode = alpha;
  material.transparent = true;
  material.depthWrite = false;
  material.blending = additive ? THREE.AdditiveBlending : THREE.NormalBlending;
  // Additive + fog ADDS the fog colour instead of fading; glow is never fogged.
  material.fog = !additive;
  return material;
}

export class ParticleSystem {
  /**
   * @param {object} opts
   * @param {number} opts.capacity total slots, allocated once (the ultra budget)
   * @param {boolean} [opts.additive]
   * @param {'glow'|'smoke'|'puff'|'shard'} [opts.shape]
   * @param {'alpha'|'erode'|'cut'} [opts.fade] how a particle dies (default by shape)
   * @param {string} [opts.name]
   */
  constructor({ capacity, additive = true, shape = SHAPES.GLOW, fade = null, name = 'particles' }) {
    this.capacity = capacity;
    this.cap = capacity;
    this.cursor = 0;
    this.live = 0;
    this.name = name;
    this.shape = shape;
    this.fade = fade ?? (shape === SHAPES.PUFF ? FADES.ERODE : shape === SHAPES.SHARD ? FADES.CUT : FADES.ALPHA);
    this.data = new Float32Array(capacity * STRIDE);
    this.sim = new Float32Array(capacity * SIM);
    this.buffer = new THREE.InstancedInterleavedBuffer(this.data, STRIDE);
    this.buffer.setUsage(THREE.DynamicDrawUsage);
    this.range = { start: 0, count: 0 };

    const geometry = new THREE.PlaneGeometry(1, 1);
    this.mesh = new THREE.Mesh(geometry, buildMaterial({ additive, shape, fade: this.fade, buffer: this.buffer }));
    this.mesh.name = `particles:${name}`;
    this.mesh.count = 0;
    this.mesh.visible = false;
    this.mesh.frustumCulled = false;
    // Above the ground shapes (telegraphs 4, FX ground shapes 5-7): debris in
    // the air belongs in front of a mark on the floor.
    this.mesh.renderOrder = 8;
    this.mesh.matrixAutoUpdate = false;
    this.mesh.userData.noOutline = true;
    this._hi = 0;
    this.stats = { emitted: 0, evicted: 0 };
  }

  attach(scene) {
    scene.add(this.mesh);
    return this;
  }

  /** The tier's cap on live particles. Slots beyond it are killed at once. */
  setCap(n) {
    const cap = Math.max(1, Math.min(this.capacity, Math.floor(n)));
    if (cap === this.cap) return;
    const { sim, data } = this;
    for (let i = cap; i < this.capacity; i++) {
      if (sim[i * SIM + LIFE] > 0) {
        sim[i * SIM + LIFE] = 0;
        data[i * STRIDE + SIZE] = 0;
        this.live--;
      }
    }
    this.cap = cap;
    if (this.cursor >= cap) this.cursor = 0;
    this._publish();
  }

  /**
   * Spawn one particle. Positional on purpose: this is called hundreds of
   * times a second from the recipes and an options object per call is garbage.
   *
   * @returns {number} the slot written
   */
  emit(x, y, z, vx, vy, vz, life, size0, size1, r, g, b, alpha = 1, drag = 1.5, gravity = 0, floor = -1e9, bounce = 0.35, stretch = 0) {
    const i = this.cursor;
    this.cursor = (i + 1) % this.cap;
    const o = i * STRIDE;
    const s = i * SIM;
    const { data, sim } = this;
    if (sim[s + LIFE] > 0) this.stats.evicted++;
    else this.live++;
    this.stats.emitted++;

    data[o + POS] = x;
    data[o + POS + 1] = y;
    data[o + POS + 2] = z;
    data[o + SIZE] = size0;
    data[o + RGBA] = r;
    data[o + RGBA + 1] = g;
    data[o + RGBA + 2] = b;
    // Erode systems carry PROGRESS in w (0 = whole); the others carry alpha.
    data[o + RGBA + 3] = this.fade === FADES.ERODE ? 0 : alpha;
    data[o + VEL] = vx;
    data[o + VEL + 1] = vy;
    data[o + VEL + 2] = vz;
    data[o + STRETCH] = stretch;

    sim[s + LIFE] = life;
    sim[s + MAX_LIFE] = life;
    sim[s + SIZE0] = size0;
    sim[s + SIZE1] = size1;
    sim[s + ALPHA0] = alpha;
    sim[s + DRAG] = drag;
    sim[s + GRAV] = gravity;
    sim[s + FLOOR] = floor;
    sim[s + BOUNCE] = bounce;
    if (i + 1 > this._hi) this._hi = i + 1;
    return i;
  }

  /** Step every live particle by `dt` seconds (0 freezes them in place). */
  update(dt) {
    if (this.live === 0) {
      if (this.mesh.visible) this._publish();
      return;
    }
    const { data, sim } = this;
    const erodeMode = this.fade === FADES.ERODE;
    const cutMode = this.fade === FADES.CUT;
    // Every live particle sits below `_hi` (emit raises it, this loop lowers
    // it), so the empty tail of the ring is never walked.
    const end = Math.min(this._hi, this.cap);
    let hi = 0;
    for (let i = 0; i < end; i++) {
      const s = i * SIM;
      let life = sim[s + LIFE];
      if (life <= 0) continue;
      const o = i * STRIDE;
      if (dt > 0) {
        life -= dt;
        if (life <= 0) {
          sim[s + LIFE] = 0;
          data[o + SIZE] = 0;
          data[o + RGBA + 3] = erodeMode ? 1 : 0;
          this.live--;
          continue;
        }
        sim[s + LIFE] = life;
        const damp = Math.exp(-sim[s + DRAG] * dt);
        let vx = data[o + VEL] * damp;
        let vy = data[o + VEL + 1] * damp - sim[s + GRAV] * dt;
        let vz = data[o + VEL + 2] * damp;
        let y = data[o + POS + 1] + vy * dt;
        const floor = sim[s + FLOOR] + FLOOR_SKIN;
        if (y < floor && sim[s + GRAV] > 0 && sim[s + BOUNCE] > 0) {
          y = floor;
          vy = -vy * sim[s + BOUNCE];
          vx *= 0.7;
          vz *= 0.7;
        }
        data[o + POS] += vx * dt;
        data[o + POS + 1] = y;
        data[o + POS + 2] += vz * dt;
        data[o + VEL] = vx;
        data[o + VEL + 1] = vy;
        data[o + VEL + 2] = vz;
        const t = 1 - life / sim[s + MAX_LIFE];
        data[o + SIZE] = sim[s + SIZE0] + (sim[s + SIZE1] - sim[s + SIZE0]) * t;
        if (erodeMode) {
          // Whole for the first third, then eaten on a smooth ramp to nothing.
          const k = t < 0.33 ? 0 : (t - 0.33) / 0.67;
          data[o + RGBA + 3] = k * k * (3 - 2 * k);
        } else if (!cutMode) {
          data[o + RGBA + 3] = sim[s + ALPHA0] * (1 - t * t);
        }
      }
      hi = i + 1;
    }
    this._hi = hi;
    this._publish();
  }

  /**
   * Hands the live prefix to the renderer as one upload range. A hidden mesh
   * costs nothing, so an empty system is invisible rather than a zero-length
   * upload (which WebGL2 would turn into a full-buffer copy).
   */
  _publish() {
    const count = this.live > 0 ? this._hi : 0;
    this.mesh.count = count;
    this.mesh.visible = count > 0;
    if (count === 0) return;
    const ranges = this.buffer.updateRanges;
    if (ranges.length === 0) ranges.push(this.range);
    this.range.start = 0;
    this.range.count = count * STRIDE;
    this.buffer.needsUpdate = true;
  }

  clear() {
    this.sim.fill(0);
    this.data.fill(0);
    this.live = 0;
    this.cursor = 0;
    this._hi = 0;
    this._publish();
  }

  dispose() {
    this.mesh.removeFromParent();
    this.mesh.geometry.dispose();
    this.mesh.material.dispose();
  }
}
